import { platformById } from "@ondit/publishing";
import {
	Button,
	Checkbox,
	ChipConfirmModal,
	ChipModal,
	ChipModalBody,
	ChipModalFooter,
	ChipModalHeader,
	ChipSelect,
	Input,
	Label,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Textarea,
} from "@ondit/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { toast } from "sonner";
import { fileUrl, mediaOptions } from "~/common/media/api";
import { useAccess, useExitAccess } from "~/core/access";
import { ApiError } from "~/core/api";
import { pick, useI18n, useT } from "~/core/i18n";
import { article as articlePath, settings } from "~/core/paths";
import { Hint, Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { PlatformPreview } from "~/common/publishing/platform-preview";
import {
	marketingKey,
	type Overview,
	overview,
	type Policy,
	pausePolicy,
	savePolicy,
	type Work,
	workCommand,
	workState,
} from "./api";
import "./marketing.css";

/**
 * The brand's distribution automation, on Settings › Publishing beside the
 * accounts it sends through. It has no clock of its own: the Weekly and Daily
 * Optimization workflows read it at their distribution step, for each page
 * they have just published.
 */
export function DistributionAutomation({ brandId }: { brandId: string }) {
	const t = useT();
	const data = useOverview(brandId);
	return (
		<Section title={t("marketing.automation")} lede={t("marketing.help")}>
			{data.error && data.data && (
				<p role="alert" className="error">
					{t("marketing.refreshFailed")}{" "}
					<Button variant="ghost" onClick={() => void data.refetch()}>
						{t("state.retry")}
					</Button>
				</p>
			)}
			<Async state={{ ...data, error: data.data ? null : data.error }}>
				{(value) => <PolicyEditor brandId={brandId} data={value} />}
			</Async>
		</Section>
	);
}

/** The posts automation produced, where Distribution keeps its history; review happens here. */
export function DistributionWork({ brandId }: { brandId: string }) {
	const t = useT();
	const data = useOverview(brandId);
	if (!data.data || (!data.data.policy && !data.data.work.length)) return null;
	return (
		<Section title={t("marketing.posts")} lede={t("marketing.postsHelp")}>
			<WorkList brandId={brandId} data={data.data} />
			<Decisions data={data.data} />
		</Section>
	);
}

function useOverview(brandId: string) {
	return useQuery({
		queryKey: marketingKey(brandId),
		queryFn: () => overview(brandId),
		refetchInterval: 10000,
	});
}
function PolicyEditor({ brandId, data }: { brandId: string; data: Overview }) {
	const t = useT();
	const { locale } = useI18n();
	const access = useAccess();
	const exit = useExitAccess();
	const cache = useQueryClient();
	const row = data.policy;
	const [base, setBase] = useState({ revision: row?.revision ?? 0, config: row?.config ?? data.defaults });
	const [config, setConfig] = useState<Policy>(base.config);
	const dirty = JSON.stringify(config) !== JSON.stringify(base.config);
	const changedElsewhere = (row?.revision ?? 0) !== base.revision;
	useEffect(() => {
		if (!dirty && changedElsewhere) {
			const next = { revision: row?.revision ?? 0, config: row?.config ?? data.defaults };
			setBase(next);
			setConfig(next.config);
		}
	}, [row, data.defaults, dirty, changedElsewhere]);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const models = useQuery({
		queryKey: ["marketing-models", brandId],
		queryFn: () => mediaOptions(brandId),
		staleTime: 60000,
	});
	const change = <K extends keyof Policy>(key: K, value: Policy[K]) => {
		setError("");
		setConfig((p) => ({ ...p, [key]: value }));
	};
	async function act(fn: () => Promise<unknown>, message = "common.saved") {
		if (busy) return;
		setBusy(true);
		setError("");
		try {
			await fn();
			await cache.invalidateQueries({ queryKey: ["marketing", brandId] });
			toast.success(t(message));
		} catch (e) {
			const message = e instanceof ApiError ? e.localized(locale) : String(e);
			setError(message);
			toast.error(message);
		} finally {
			setBusy(false);
		}
	}
	const platforms = data.platforms ?? [];
	const missing =
		config.enabled &&
		(config.channels.length === 0 ||
			config.channels.some((c) => {
				const platform = platforms.find((p) => p.id === c.platform);
				return (
					(config.mode === "automatic" && (!c.credential_id || !platform?.methods.includes("api"))) ||
					platform?.preview.fields.some(
						(f) =>
							f.required &&
							!["title", "body", "text", "description", "excerpt"].includes(f.key) &&
							!c.fields[f.key]?.trim(),
					)
				);
			}));
	async function save() {
		const result = await savePolicy(brandId, base.revision, config);
		await cache.invalidateQueries({ queryKey: ["marketing", brandId] });
		setBase({ revision: result.policy.revision, config: result.policy.config });
		setConfig(result.policy.config);
	}
	return (
		<>
			<div className="marketing-actions">
				<span>
					{t(
						row?.config.enabled ? `marketing.mode.${row.config.mode}` : row ? "marketing.paused" : "marketing.disabled",
					)}
				</span>
				{row?.config.enabled && (
					<Button
						variant="ghost"
						disabled={!exit.editor || busy}
						onClick={() => void act(() => pausePolicy(brandId, row.revision), "marketing.paused")}
					>
						{t("marketing.pause")}
					</Button>
				)}
				{row && !row.config.enabled && (
					<Button
						variant="default"
						disabled={!access.editor || busy || dirty}
						onClick={() =>
							void act(() => savePolicy(brandId, row.revision, { ...row.config, enabled: true }), "marketing.resumed")
						}
					>
						{t("marketing.resume")}
					</Button>
				)}
			</div>
			{row && (
				<p className="hint">
					{t("marketing.weeklyUsage", { spent: row.weekly_spent, held: row.held, limit: row.config.weekly_credits })}
				</p>
			)}
			{dirty && <p className="hint">{t("marketing.unsaved")}</p>}
			<details open={!row}>
				<summary>{t("marketing.configure")}</summary>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						if (!missing && !changedElsewhere) void act(save);
					}}
				>
					<fieldset disabled={!access.editor || busy} className="bare-fieldset marketing-policy">
						<label htmlFor={`marketing-${brandId}-1`} className="marketing-check">
							<Checkbox
								id={`marketing-${brandId}-1`}
								checked={config.enabled}
								onCheckedChange={(v) => change("enabled", v === true)}
							/>
							{t("marketing.enabled")}
						</label>
						<div className="marketing-fields">
							<label htmlFor={`marketing-${brandId}-2`}>
								<span>{t("marketing.mode")}</span>
								<ChipSelect
									fullWidth
									dropdownWidth="trigger"
									id={`marketing-${brandId}-2`}
									aria-label={t("marketing.mode")}
									value={config.mode}
									onChange={(v) => change("mode", v as Policy["mode"])}
									options={["review", "automatic"].map((value) => ({
										value,
										label: t(`marketing.mode.${value}`),
									}))}
								/>
							</label>
							<label htmlFor={`marketing-${brandId}-3`}>
								<span>
									{t("marketing.budget")} <Hint text={t("marketing.budgetHelp")} />
								</span>
								<Input
									id={`marketing-${brandId}-3`}
									type="number"
									min={1}
									max={1000000}
									required
									value={config.weekly_credits}
									onChange={(e) => change("weekly_credits", Number(e.target.value))}
								/>
							</label>
							<label htmlFor={`marketing-${brandId}-5`}>
								<span>{t("marketing.pending")}</span>
								<Input
									id={`marketing-${brandId}-5`}
									type="number"
									min={1}
									max={20}
									required
									value={config.max_pending}
									onChange={(e) => change("max_pending", Number(e.target.value))}
								/>
							</label>
							<label htmlFor={`marketing-${brandId}-7`}>
								<span>{t("marketing.image")}</span>
								<ChipSelect
									fullWidth
									dropdownWidth="trigger"
									id={`marketing-${brandId}-7`}
									aria-label={t("marketing.image")}
									value={config.image_model || "none"}
									onChange={(v) => change("image_model", v === "none" ? "" : v)}
									options={[
										{ value: "none", label: t("marketing.noImage") },
										...(models.data?.models
											.filter((m) => m.modality === "image")
											.map((m) => ({ value: m.id, label: m.name })) ?? []),
									]}
								/>
							</label>
							{config.image_model && (
								<label htmlFor={`marketing-${brandId}-8`}>
									<span>{t("marketing.preset")}</span>
									<ChipSelect
										fullWidth
										dropdownWidth="trigger"
										id={`marketing-${brandId}-8`}
										aria-label={t("marketing.preset")}
										value={config.preset}
										onChange={(v) => change("preset", v)}
										options={["square", "portrait", "vertical", "landscape"].map((value) => ({
											value,
											label: t(`marketing.preset.${value}`),
										}))}
									/>
								</label>
							)}
						</div>

						{config.mode === "automatic" && <p className="hint">{t("marketing.automaticHelp")}</p>}
						<div className="marketing-channel-list">
							<Label>{t("marketing.channels")}</Label>
							{config.channels.map((channel, index) => {
								const platform = platforms.find((p) => p.id === channel.platform);
								const update = (patch: Partial<typeof channel>) =>
									change(
										"channels",
										config.channels.map((c, i) => (i === index ? { ...c, ...patch } : c)),
									);
								return (
									<section key={channel.platform} className="marketing-channel">
										<div className="marketing-actions">
											<strong>{platform?.name ?? channel.platform}</strong>
											<Button
												type="button"
												variant="ghost"
												onClick={() =>
													change(
														"channels",
														config.channels.filter((_, i) => i !== index),
													)
												}
											>
												{t("common.remove")}
											</Button>
										</div>
										<div className="marketing-fields">
											<label htmlFor={`marketing-${brandId}-${channel.platform}-9`}>
												<span>{t("marketing.account")}</span>
												<ChipSelect
													fullWidth
													dropdownWidth="trigger"
													id={`marketing-${brandId}-${channel.platform}-9`}
													aria-label={`${platform?.name ?? channel.platform} ${t("marketing.account")}`}
													value={channel.credential_id ?? "manual"}
													onChange={(v) => update({ credential_id: v === "manual" ? null : v })}
													options={[
														{ value: "manual", label: t("marketing.manualDelivery") },
														...(data.credentials ?? [])
															.filter((c) => c.provider_id === platform?.credential_provider)
															.map((c) => ({ value: c.id, label: c.account || c.name })),
													]}
												/>
												{platform?.methods.includes("api") && (
													<a href={settings(brandId, "integrations")} target="_blank" rel="noreferrer">
														{t("marketing.connectAccount")}
													</a>
												)}
											</label>
											{config.mode === "automatic" &&
												(!channel.credential_id || !platform?.methods.includes("api")) && (
													<p className="error">
														{t(
															platform?.methods.includes("api")
																? "marketing.accountRequired"
																: "marketing.apiUnsupported",
														)}
													</p>
												)}
											{platform?.dynamic_host && (
												<label htmlFor={`marketing-${brandId}-${channel.platform}-10`}>
													<span>{t("marketing.destination")}</span>
													<Input
														id={`marketing-${brandId}-${channel.platform}-10`}
														type="url"
														required
														value={channel.target_url}
														onChange={(e) => update({ target_url: e.target.value })}
													/>
												</label>
											)}
											{(platform?.preview.fields ?? [])
												.filter((f) => !["title", "body", "text", "description", "excerpt"].includes(f.key))
												.map((f) => (
													<label htmlFor={`marketing-${brandId}-${channel.platform}-${f.key}`} key={f.key}>
														<span>
															{pick(locale, f.label)}
															{f.required ? " *" : ""}
														</span>
														{f.options?.length ? (
															<ChipSelect
																fullWidth
																dropdownWidth="trigger"
																id={`marketing-${brandId}-${channel.platform}-${f.key}`}
																value={channel.fields[f.key] || "unset"}
																onChange={(v) =>
																	update({ fields: { ...channel.fields, [f.key]: v === "unset" ? "" : v } })
																}
																options={[
																	{ value: "unset", label: t("marketing.unset") },
																	...f.options.map((value) => ({
																		value,
																		label: f.option_labels?.[value] ? pick(locale, f.option_labels[value]) : value,
																	})),
																]}
															/>
														) : (
															<Input
																id={`marketing-${brandId}-${channel.platform}-${f.key}`}
																value={channel.fields[f.key] ?? ""}
																required={f.required}
																onChange={(e) => update({ fields: { ...channel.fields, [f.key]: e.target.value } })}
															/>
														)}
													</label>
												))}
										</div>
									</section>
								);
							})}
							<ChipSelect
								fullWidth
								dropdownWidth="trigger"
								value=""
								aria-label={t("marketing.addChannel")}
								placeholder={t("marketing.addChannel")}
								searchable
								onChange={(platform) => {
									const p = platforms.find((p) => p.id === platform);
									change("channels", [
										...config.channels,
										{ platform, credential_id: null, target_url: p?.entry_url ?? "", fields: {} },
									]);
								}}
								options={platforms
									.filter(
										(p) =>
											!config.channels.some((c) => c.platform === p.id) &&
											(config.mode !== "automatic" || p.methods.includes("api")),
									)
									.map((p) => ({ value: p.id, label: p.name }))}
							/>
						</div>
						<div className="marketing-save">
							{changedElsewhere && (
								<p role="alert" className="error">
									{t("marketing.changedElsewhere")}{" "}
									<Button
										type="button"
										variant="ghost"
										onClick={() => {
											setBase({ revision: row?.revision ?? 0, config: row?.config ?? data.defaults });
											setConfig(row?.config ?? data.defaults);
										}}
									>
										{t("marketing.reloadSettings")}
									</Button>
								</p>
							)}
							{missing && (
								<p className="error">
									{t(config.channels.length ? "marketing.completeSettings" : "marketing.chooseChannel")}
								</p>
							)}
							<Button type="submit" variant="primary" disabled={missing || changedElsewhere || (!!row && !dirty)}>
								{t(busy ? "marketing.saving" : "common.save")}
							</Button>
						</div>
					</fieldset>
				</form>
			</details>
			{error && (
				<p role="alert" className="error">
					{error}
				</p>
			)}
		</>
	);
}
function Decisions({ data }: { data: Overview }) {
	const t = useT();
	const { locale } = useI18n();
	if (!data.decisions.length) return null;
	return (
		<details>
			<summary>{t("marketing.decisions")}</summary>
			{data.decisions.map((d) => (
				<p key={d.id}>
					{new Date(d.created_at).toLocaleString(locale)} · {d.title} · {t(`marketing.reason.${d.reason}`)}
				</p>
			))}
		</details>
	);
}
function unavailableReason(data: Overview, work: Work): string {
	if (work.status === "cancelled") return "marketing.state.cancelled";
	if (data.policy && !data.policy.config.enabled) return "marketing.paused";
	return "marketing.settingsChanged";
}
function WorkList({ brandId, data }: { brandId: string; data: Overview }) {
	const t = useT();
	const { locale } = useI18n();
	// The to-do list opens a work's review directly.
	const [params] = useSearchParams();
	const [selected, setSelected] = useState<string | null>(params.get("work"));
	const selectedWork = data.work.find((work) => work.id === selected);
	if (!data.work.length)
		return <p className="hint">{t(data.policy?.config.enabled ? "marketing.awaitingRun" : "marketing.empty")}</p>;
	return (
		<>
			<div className="table-scroll">
				<Table variant="resource">
					<TableHeader>
						<TableRow>
							<TableHead>{t("marketing.content")}</TableHead>
							<TableHead>{t("marketing.state")}</TableHead>
							<TableHead>{t("marketing.cost")}</TableHead>
							<TableHead>{t("marketing.next")}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{data.work.map((work) => (
							<TableRow key={work.id}>
								<TableCell>
									<Link to={articlePath(brandId, work.page_id)}>{work.title}</Link>
									<div className="hint">
										{new Date(work.created_at).toLocaleString(locale)} ·{" "}
										{work.config.channels.map((c) => platformById(c.platform)?.name ?? c.platform).join(", ")}
									</div>
								</TableCell>
								<TableCell>
									{t(`marketing.state.${workState(work)}`)}
									{!work.allowed && work.status !== "cancelled" && (
										<div className="hint">{t(unavailableReason(data, work))}</div>
									)}
									{work.reason && (
										<div className="hint">
											{work.reason.startsWith("errors.") ? t(work.reason) : t(`marketing.reason.${work.reason}`)}
										</div>
									)}
								</TableCell>
								<TableCell>
									{work.spent}
									{work.held > 0 && <div className="hint">{t("marketing.held", { n: work.held })}</div>}
								</TableCell>
								<TableCell>
									<div className="record-actions">
										{work.variants.length > 0 && (
											<Button variant="default" size="sm" onClick={() => setSelected(work.id)}>
												{t(work.status === "ready" ? "marketing.viewDelivery" : "marketing.review")}
											</Button>
										)}
										{!work.variants.length && work.status !== "blocked" && (
											<Link to={`/w/${brandId}/tasks/agent`}>{t("marketing.runDetails")}</Link>
										)}
									</div>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
			{selectedWork && (
				<Review
					brandId={brandId}
					work={selectedWork}
					unavailable={unavailableReason(data, selectedWork)}
					onClose={() => setSelected(null)}
				/>
			)}
		</>
	);
}
function Review({
	brandId,
	work,
	onClose,
	unavailable,
}: {
	brandId: string;
	work: Work;
	onClose: () => void;
	unavailable: string;
}) {
	const t = useT();
	const { locale } = useI18n();
	const access = useAccess();
	const exit = useExitAccess();
	const cache = useQueryClient();
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [stopping, setStopping] = useState(false);
	const [confirmed, setConfirmed] = useState<string | null>(null);
	const [outcome, setOutcome] = useState(work.outcome);
	const [evidence, setEvidence] = useState(work.evidence);
	async function command(body: { action: string; [key: string]: unknown }, close = false) {
		if (busy) return;
		setBusy(true);
		setError("");
		try {
			await workCommand(brandId, work.id, body);
			toast.success(t(body.action === "approve" ? "marketing.approved" : "common.saved"));
			await cache.invalidateQueries({ queryKey: ["marketing", brandId] });
			if (close) onClose();
			else if (body.action !== "observe") setConfirmed(null);
		} catch (e) {
			const message = e instanceof ApiError ? e.localized(locale) : String(e);
			setError(message);
			toast.error(message);
		} finally {
			setBusy(false);
		}
	}
	// The posts are the page revision's platform versions, which the overview
	// this row came from already carries and refreshes.
	const content = { revision: work.current_revision, variants: work.variants };
	const snapshot = JSON.stringify([content.revision, content.variants.map((v) => [v.id, v.revision])]);
	const checked = snapshot !== null && confirmed === snapshot;
	const ready = work.config.channels.every((c) => content.variants.some((v) => v.platform === c.platform));
	const needsApproval =
		work.status !== "ready" ||
		work.config.channels.some((c) => !content.variants.find((v) => v.platform === c.platform)?.task_id);
	const missingVariants = !ready;
	return (
		<>
			<ChipModal
				open
				onOpenChange={(open) => {
					if (!open) onClose();
				}}
				srTitle={t("marketing.review")}
				size="xl"
				dismissDisabled={busy}
			>
				<ChipModalHeader onClose={onClose}>{work.title}</ChipModalHeader>
				<ChipModalBody>
					{!work.allowed && (
						<p className="hint">
							{t(unavailable)}{" "}
							<Link to={settings(brandId, "publishing")} onClick={onClose}>
								{t("marketing.openSettings")}
							</Link>
						</p>
					)}
					{missingVariants && <p className="error">{t("marketing.needsAdapt")}</p>}

					<div className="marketing-actions">
						<Link to={articlePath(brandId, work.page_id)} onClick={onClose}>
							{t("marketing.edit")}
						</Link>
						<Button
							variant="ghost"
							disabled={!access.editor || busy || !work.allowed || !missingVariants}
							onClick={() => void command({ action: "adapt", revision: content.revision }, false)}
						>
							{t("marketing.refresh")}
						</Button>
						<Button
							variant="ghost"
							disabled={!exit.editor || busy || work.status === "cancelled"}
							onClick={() => setStopping(true)}
						>
							{t("marketing.cancelWork")}
						</Button>
					</div>
					{work.config.channels.map((channel) => {
						const variant = content.variants.find((v) => v.platform === channel.platform);
						const platform = platformById(channel.platform);
						return variant && platform ? (
							<div key={variant.id} className="marketing-review">
								<PlatformPreview
									platform={platform}
									payload={{
										version: 1,
										platform: platform.id,
										fields: variant.document.fields,
										media: variant.document.media.map((m) => ({ url: fileUrl(m.file_id), alt: m.alt })),
										source: { page_id: work.page_id, revision: content.revision, canonical: "" },
										target_url: channel.target_url,
									}}
									resolvedFiles={Object.fromEntries(variant.document.media.map((m) => [m.file_id, fileUrl(m.file_id)]))}
									resolvedMedia={variant.document.media
										.filter((m) => m.role === "image" || m.role === "video")
										.map((m) => ({ url: fileUrl(m.file_id), alt: m.alt, kind: m.role as "image" | "video" }))}
								/>
								<p className="hint">
									{variant.task_id
										? t(`marketing.state.${work.variants.find((v) => v.id === variant.id)?.state ?? "ready"}`)
										: channel.credential_id
											? t("marketing.apiAfterApproval")
											: t("marketing.browserAfterApproval")}
								</p>
								{variant.task_id && (
									<Link to={`/w/${brandId}/publish/distribution?tab=prepare&task=${variant.task_id}`} onClick={onClose}>
										{t("marketing.openTask")}
									</Link>
								)}
								{work.variants.find((v) => v.id === variant.id)?.result_url && (
									<a
										href={work.variants.find((v) => v.id === variant.id)!.result_url!}
										target="_blank"
										rel="noreferrer"
									>
										{t("marketing.openPublished")}
									</a>
								)}
							</div>
						) : (
							<p key={channel.platform}>
								{platform?.name}: {t("marketing.needsAdapt")}
							</p>
						);
					})}

					<details>
						<summary>{t("marketing.observe")}</summary>
						<fieldset disabled={!access.editor || busy} className="bare-fieldset marketing-policy">
							<ChipSelect
								fullWidth
								dropdownWidth="trigger"
								aria-label={t("marketing.observe")}
								value={outcome}
								onChange={setOutcome}
								options={["unmeasured", "insufficient", "improved", "unchanged", "worse"].map((value) => ({
									value,
									label: t(`marketing.outcome.${value}`),
								}))}
							/>
							<Label htmlFor={`marketing-${work.id}-evidence`}>{t("marketing.evidence")}</Label>
							<Textarea
								id={`marketing-${work.id}-evidence`}
								value={evidence}
								onChange={(e) => setEvidence(e.target.value)}
								maxLength={4000}
							/>
							<p className="hint">{t("marketing.evidenceHelp")}</p>
							<Button
								variant="default"
								disabled={outcome !== "unmeasured" && !evidence.trim()}
								onClick={() => void command({ action: "observe", outcome, evidence }, false)}
							>
								{t("common.save")}
							</Button>
						</fieldset>
					</details>

					{error && (
						<p role="alert" className="error">
							{error}
						</p>
					)}
				</ChipModalBody>
				<ChipModalFooter
					defaultAction="dismiss"
					onCancel={onClose}
					cancelLabel={t("common.close")}
					leadingContent={
						needsApproval ? (
							<label htmlFor={`marketing-${brandId}-12`} className="marketing-check">
								<Checkbox
									id={`marketing-${brandId}-12`}
									checked={checked}
									onCheckedChange={(v) => setConfirmed(v === true ? snapshot : null)}
									disabled={!access.editor || busy || !work.allowed || !ready}
								/>
								{t("marketing.confirmRevision", { n: content.revision })}
							</label>
						) : undefined
					}
					primaryAction={
						needsApproval
							? {
									label: t(busy ? "marketing.working" : "marketing.approve"),
									disabled: !checked || !ready || !access.editor || busy || !work.allowed,
									onClick: () => {
										void command({
											action: "approve",
											revision: content.revision,
											variants: content.variants.map((v) => ({ id: v.id, revision: v.revision })),
										});
									},
								}
							: undefined
					}
				/>
			</ChipModal>
			<ChipConfirmModal
				open={stopping}
				onOpenChange={setStopping}
				title={t("marketing.cancelWork")}
				text={t("marketing.stopHelp")}
				dismissLabel={t("common.cancel")}
				confirm={{
					label: t("marketing.cancelWork"),
					pending: busy,
					onClick: () => void command({ action: "cancel" }, true),
				}}
			/>
		</>
	);
}
